import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { SaveTableViewInput, TableViewRow, TableViewScreen, UpdateTableViewInput } from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from './api-client';

// S32 (DECISIONS-LOG S29–S32 Q28): named views on the record tables, private or shared. ADR 0049: a
// Ranking view belongs to one job, so its list is asked for, and cached, per job.

const viewsKey = (screen: TableViewScreen, jobId: string | null): readonly (string | null)[] => ['saved-views', screen, jobId];

export function useTableViews(screen: TableViewScreen, isEnabled: boolean, jobId: string | null = null): UseQueryResult<readonly TableViewRow[], ApiError> {
  return useQuery<readonly TableViewRow[], ApiError>({
    queryKey: viewsKey(screen, jobId),
    queryFn: () => apiGet<readonly TableViewRow[]>('/saved-views', { query: jobId ? { screen, jobId } : { screen } }),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
    staleTime: 60_000,
  });
}

export function useSaveTableView(screen: TableViewScreen, jobId: string | null = null) {
  const queryClient = useQueryClient();
  return useMutation<TableViewRow, ApiError, Omit<SaveTableViewInput, 'jobId'>>({
    mutationFn: (input) => apiPost<TableViewRow>('/saved-views', { ...input, jobId }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: viewsKey(screen, jobId) });
    },
  });
}

export function useUpdateTableView(screen: TableViewScreen, jobId: string | null = null) {
  const queryClient = useQueryClient();
  return useMutation<TableViewRow, ApiError, { readonly id: string; readonly input: UpdateTableViewInput }>({
    mutationFn: ({ id, input }) => apiPatch<TableViewRow>(`/saved-views/${id}`, input),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: viewsKey(screen, jobId) });
    },
  });
}

export function useDeleteTableView(screen: TableViewScreen, jobId: string | null = null) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, TableViewRow, { readonly previous: readonly TableViewRow[] | undefined }>({
    mutationFn: (view) => apiDelete<void>(`/saved-views/${view.id}`),
    onMutate: async (view) => {
      await queryClient.cancelQueries({ queryKey: viewsKey(screen, jobId) });
      const previous = queryClient.getQueryData<readonly TableViewRow[]>(viewsKey(screen, jobId));
      if (previous) queryClient.setQueryData(viewsKey(screen, jobId), previous.filter((row) => row.id !== view.id));
      return { previous };
    },
    onError: (_error, _view, context) => {
      if (context?.previous) queryClient.setQueryData(viewsKey(screen, jobId), context.previous);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: viewsKey(screen, jobId) });
    },
  });
}
